import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { login as apiLogin, logout as apiLogout, fetchMe } from '../api/auth';
import { getToken, setToken, clearToken } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(new Set());
  const [roles, setRoles] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  const applyUser = (u) => {
    setUser(u);
    setPermissions(new Set(u?.permissions || []));
    setRoles(new Set(u?.roles || []));
  };

  const clearAuth = useCallback(() => {
    clearToken();
    setUser(null);
    setPermissions(new Set());
    setRoles(new Set());
  }, []);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setIsLoading(false);
      return;
    }
    try {
      const me = await fetchMe();
      applyUser(me);
    } catch {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuth]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    window.addEventListener('auth:unauthorized', clearAuth);
    return () => window.removeEventListener('auth:unauthorized', clearAuth);
  }, [clearAuth]);

  const login = async (email, password) => {
    const { token, user: u } = await apiLogin(email, password);
    setToken(token);
    applyUser(u);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // Token may already be invalid — still clear local state below.
    }
    clearAuth();
  };

  const isSuperAdmin = roles.has('super-admin');

  const value = {
    user,
    permissions,
    roles,
    isSuperAdmin,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
